import { useSession } from "@clerk/clerk-react";
import axios from "axios";
import { useCallback } from "react";
import { toast } from "sonner";
import { useAppDispatch } from "@/store/store";
import { onProjectsSnapshot } from "@/store/supabaseSlice";
import { track } from "@/utils/events/tracker";
import {
  Language,
  ProjectRunMode,
  ReactFramework,
} from "@/utils/templateProjects";
import {
  getAxiosErorrMessage,
  getUnexpiredToken,
  logAxiosError,
  useUserId,
} from "@/utils/utils";

// when pages lands we can move the storyboard config into the repo
const tutorialParams = {
  repoName: "Tempo Tutorial",
  repoPath: "TempoLabsAI/tempo-tutorial-canvas",
  framework: ReactFramework.VITE,
  nodeVersion: "22",
  packageManager: "npm",
  runMode: ProjectRunMode.CLOUD,
  environmentVariables: {},
  language: Language.Typescript,
  initialStoryboards: [
    {
      x: -756,
      y: -491,
      id: "270f2b72-c799-45c9-aa03-dc6c521d1d41",
      type: "APPLICATION",
      width: 1512,
      height: 982,
      zIndex: 0,
      fitCanvasToStoryboard: 0.6,
    },
    {
      x: 974.7935029161135,
      y: -503.9003687809183,
      id: "0eadecf9-eaf4-4248-8d98-53f1876c205f",
      name: "Background",
      type: "COMPONENT",
      route:
        "/tempobook?id=eb4cd2e6-33b1-4618-bf10-e22b192d1cd2&iframeId=eb4cd2e6-33b1-4618-bf10-e22b192d1cd2",
      width: 1440,
      height: 1024,
      zIndex: 10,
      created: true,
      created_at: "2025-11-11T20:06:51.753Z",
      relativeFilePath:
        "src/tempobook/storyboards/0eadecf9-eaf4-4248-8d98-53f1876c205f/index.tsx",
      scrollToStoryboard: false,
    },
    {
      x: 2628,
      y: -492.2421492198748,
      id: "c25490ac-8bf9-41f2-b10f-ec17df7dd2fe",
      name: "1. Edit code visually",
      type: "COMPONENT",
      route:
        "/tempobook?id=99d07ecb-fb26-41b0-817a-18617c12d0f0&iframeId=99d07ecb-fb26-41b0-817a-18617c12d0f0",
      width: 1440,
      height: 4831,
      zIndex: 20,
      created: true,
      created_at: "2025-11-11T20:44:09.954Z",
      relativeFilePath:
        "src/tempobook/storyboards/c25490ac-8bf9-41f2-b10f-ec17df7dd2fe/index.tsx",
      scrollToStoryboard: false,
    },
    {
      x: 4482.684396228583,
      y: -507.9999999999998,
      id: "ede848df-f5c4-47cd-9a48-de63bc9ef4a4",
      name: "2. Vibe-code anytime",
      type: "COMPONENT",
      route:
        "/tempobook?id=85b0a064-a5d2-4a3b-90d8-3af9fd9003b3&iframeId=85b0a064-a5d2-4a3b-90d8-3af9fd9003b3",
      width: 1440,
      height: 4840,
      zIndex: 40,
      created: true,
      created_at: "2025-11-12T13:41:33.170Z",
      relativeFilePath:
        "src/tempobook/storyboards/ede848df-f5c4-47cd-9a48-de63bc9ef4a4/index.tsx",
      createdHotReloadTs: 1762954892769,
    },
    {
      x: 6234.131586198549,
      y: -517.516448064408,
      id: "e1a7adc0-bf96-4884-9f2b-bf76d1f0f574",
      name: "3. Build & Maintain Design Systems",
      type: "COMPONENT",
      route:
        "/tempobook?id=536eeab2-e4e5-4c7e-a373-3e15ce2e30ca&iframeId=536eeab2-e4e5-4c7e-a373-3e15ce2e30ca",
      width: 1440,
      height: 4703,
      zIndex: 60,
      created: true,
      created_at: "2025-11-12T14:10:54.380Z",
      relativeFilePath:
        "src/tempobook/storyboards/e1a7adc0-bf96-4884-9f2b-bf76d1f0f574/index.tsx",
      createdHotReloadTs: 1762956653962,
    },
    {
      x: 7979,
      y: -517,
      id: "742a75ce-4e4a-43cc-a924-0d5fd8291588",
      name: "4. Collaborate on production code",
      type: "COMPONENT",
      route:
        "/tempobook?id=5f1036fe-a614-4852-8592-ced0de895ddd&iframeId=5f1036fe-a614-4852-8592-ced0de895ddd",
      width: 1440,
      height: 4181,
      zIndex: 80,
      created: true,
      created_at: "2025-11-12T14:48:42.591Z",
      relativeFilePath:
        "src/tempobook/storyboards/742a75ce-4e4a-43cc-a924-0d5fd8291588/index.tsx",
      createdHotReloadTs: 1762958922234,
    },
    {
      x: 9865,
      y: -543,
      id: "3c8dcfe4-41cf-4bc1-b967-e7a83a8dd1ce",
      name: "5. Escape the constraints of code (when needed)",
      type: "COMPONENT",
      route:
        "/tempobook?id=536eeab2-e4e5-4c7e-a373-3e15ce2e30ca&iframeId=536eeab2-e4e5-4c7e-a373-3e15ce2e30ca",
      width: 1440,
      height: 3580,
      zIndex: 100,
      created: true,
      created_at: "2025-11-12T15:15:28.687Z",
      relativeFilePath:
        "src/tempobook/storyboards/3c8dcfe4-41cf-4bc1-b967-e7a83a8dd1ce/index.tsx",
      createdHotReloadTs: 1762960526949,
    },
  ],
};

export const useCreateTutuorialCanvas = () => {
  const userId = useUserId();
  const { session } = useSession();
  const dispatch = useAppDispatch();

  const createTutorialCanvas = useCallback(async () => {
    try {
      if (!userId) {
        throw new Error("User not found");
      }

      const supabaseJWT = await getUnexpiredToken(session);
      const { project, canvas } = (
        await axios.post(
          `${import.meta.env.VITE_BACKEND_BASE_URL}/projects/add`,
          { ...tutorialParams, userId },
          {
            headers: {
              Authorization: `Bearer ${supabaseJWT}`,
            },
          },
        )
      ).data;

      dispatch(
        onProjectsSnapshot({
          newItems: [project],
          deletedItems: [],
        }),
      );

      window.open(`/canvases/${canvas.id}/editor?open-design-mode=t`, "_blank");
    } catch (error) {
      logAxiosError("Error creating new project", error);

      // Track project creation failure
      track("PROJECT_CREATION_STEP", {
        step: "PROJECT_CREATION_FAILED",
        canvas_id: null,
        error: getAxiosErorrMessage(error),
        tuturialCanvas: true,
      });

      toast.error(
        `Could not create a new project: ${getAxiosErorrMessage(error)}`,
      );
    }
  }, [dispatch, session, userId]);

  return createTutorialCanvas;
};
